import { WarrantyValidation } from "@dune/types";
import { Button } from "@dune/ui";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUpdateConstructionSiteSubmissionBinderWarrantiesSelected } from "../../../api/mutations/constructionSiteSubmission.mutations";
import { useSubmittableConstructionSiteBindersAndWarranties } from "../../../api/queries/constructionSite.queries";
import { useBinderWarrantiesTypesSelected } from "../../../api/queries/constructionSiteSubmission.queries";
import { useMe } from "../../../api/queries/users.queries";
import { useRequiredParams } from "../../../hooks/useRequiredParams";
import BinderWarrantyOfferContainer from "../offers/BinderWarrantyOfferContainer";
import { PricingOptionsProvider } from "../offers/contexts/PricingOptionsContext";
import { toast } from "sonner";
import { getErrorMessage, RoleEnum } from "@dune/vars";

const getSelectableWarrantiesBasedOnCurrentSelection = (
	selectedBinderWarrantyIds: number[],
	availableBinderWarranties: WarrantyValidation[],
): number[] => {
	const selectedBinderWarranties = availableBinderWarranties.filter(
		(availableBinderWarranty) =>
			selectedBinderWarrantyIds.includes(availableBinderWarranty.id),
	);

	const selectedMRT = selectedBinderWarranties.find(
		(selectedBinderWarranty) => selectedBinderWarranty.warrantyType === "MRT",
	);
	const selectedDO = selectedBinderWarranties.find(
		(selectedBinderWarranty) => selectedBinderWarranty.warrantyType === "DO",
	);
	const selectedTRC = selectedBinderWarranties.find(
		(selectedBinderWarranty) => selectedBinderWarranty.warrantyType === "TRC",
	);

	const availableDOs = availableBinderWarranties.filter(
		(availableBinderWarranty) => availableBinderWarranty.warrantyType === "DO",
	);
	const availableTRCs = availableBinderWarranties.filter(
		(availableBinderWarranty) => availableBinderWarranty.warrantyType === "TRC",
	);
	const availablePJIds = availableBinderWarranties
		.filter(
			(availableBinderWarranty) =>
				availableBinderWarranty.warrantyType === "PJ",
		)
		.map((warranty) => warranty.id);

	if (selectedMRT) {
		// PJ est complémentaire à MRT : sélectionnable si MRT est sélectionné
		return availablePJIds;
	}

	if (selectedDO) {
		const availableTRCsWithSameInsurer = availableTRCs.filter(
			(availableBinderWarranty) =>
				availableBinderWarranty.binderAmendment.binder.insurer.society.siren ===
				selectedDO.binderAmendment.binder.insurer.society.siren,
		);
		// PJ est complémentaire à DO : sélectionnable si DO est sélectionné
		return [
			...availableTRCsWithSameInsurer.map((warranty) => warranty.id),
			...availablePJIds,
		];
	}

	if (selectedTRC) {
		const availableDOsWithSameInsurer = availableDOs.filter(
			(availableBinderWarranty) =>
				availableBinderWarranty.binderAmendment.binder.insurer.society.siren ===
				selectedTRC.binderAmendment.binder.insurer.society.siren,
		);
		return availableDOsWithSameInsurer.map((warranty) => warranty.id);
	}

	// Rien sélectionné : PJ est grisé (non sélectionnable sans DO ou MRT)
	return availableBinderWarranties
		.filter(
			(availableBinderWarranty) =>
				availableBinderWarranty.warrantyType !== "PJ",
		)
		.map((warranty) => warranty.id);
};

const isWarrantyDisabled = (
	binderWarranty: WarrantyValidation,
	selectedBinderWarrantyIds: number[],
	availableBinderWarranties: WarrantyValidation[],
): boolean => {
	const isCurrentlySelected = selectedBinderWarrantyIds.includes(
		binderWarranty.id,
	);

	if (isCurrentlySelected) {
		return false;
	}

	const selectableWarranties = getSelectableWarrantiesBasedOnCurrentSelection(
		selectedBinderWarrantyIds,
		availableBinderWarranties,
	);

	return !selectableWarranties.includes(binderWarranty.id);
};
interface OfferSelectionProps {
	handleNextStep: () => void;
	isUpdateDisabled?: boolean;
}

export default function OfferSelection({
	handleNextStep,
	isUpdateDisabled,
}: OfferSelectionProps) {
	const { constructionSiteId } = useRequiredParams<{
		constructionSiteId: string;
	}>();
	const navigate = useNavigate();
	const { me } = useMe();

	const { data: submittableBindersAndWarranties } =
		useSubmittableConstructionSiteBindersAndWarranties(constructionSiteId);

	const { data: selectedBinderWarranties } = useBinderWarrantiesTypesSelected(
		Number(constructionSiteId),
	);

	const updateBinderWarrantiesSelectedMutation =
		useUpdateConstructionSiteSubmissionBinderWarrantiesSelected(
			Number(constructionSiteId),
		);

	const [localSelectedBinderWarrantyIds, setLocalSelectedBinderWarrantyIds] =
		useState<number[]>([]);

	useEffect(() => {
		if (!selectedBinderWarranties) return;
		setLocalSelectedBinderWarrantyIds((current) => {
			if (current.length > 0) return current;
			return selectedBinderWarranties.map((bw) => bw.id);
		});
	}, [selectedBinderWarranties]);

	const handleBinderWarrantyCheckboxChange = (id: number) => {
		if (!submittableBindersAndWarranties) return;

		setLocalSelectedBinderWarrantyIds((prev) => {
			if (prev.includes(id)) {
				return prev.filter((warrantyId) => warrantyId !== id);
			}

			const selectableWarranties =
				getSelectableWarrantiesBasedOnCurrentSelection(
					prev,
					submittableBindersAndWarranties.flatMap((bw) => bw.warranties),
				);

			if (!selectableWarranties.includes(id)) {
				return prev;
			}

			return [...prev, id];
		});
	};

	const handleSave = async () => {
		try {
			const selectedIds = [...localSelectedBinderWarrantyIds];
			!isUpdateDisabled &&
				(await updateBinderWarrantiesSelectedMutation.mutateAsync(selectedIds));
			handleNextStep();
		} catch (error) {
			const message = getErrorMessage(error);
			toast.error(
				"Erreur lors de la sauvegarde des garanties sélectionnées: ",
				{
					description: message,
				},
			);
		}
	};

	if (!submittableBindersAndWarranties) {
		return <div>Chargement...</div>;
	}

	return (
		<PricingOptionsProvider>
			<div className="space-y-6">
				<h1 className="text-[#144C65] text-2xl font-medium mb-4">
					Sélectionnez l'offre de votre choix
				</h1>
				<div className="text-gray-500 text-sm mb-6">
					Choisissez l'offre d'assurance qui correspond le mieux à votre projet
				</div>

				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
					{submittableBindersAndWarranties
						.flatMap((binderWarranty) => binderWarranty.warranties)
						.map((binderWarranty) => {
							return (
								<BinderWarrantyOfferContainer
									key={binderWarranty.id}
									binderWarranty={binderWarranty}
									constructionSiteId={constructionSiteId}
									storedPricingType="INITIAL"
									handleBinderWarrantyCheckboxChange={
										handleBinderWarrantyCheckboxChange
									}
									checked={localSelectedBinderWarrantyIds.includes(
										binderWarranty.id,
									)}
									isDisabled={isWarrantyDisabled(
										binderWarranty,
										localSelectedBinderWarrantyIds,
										submittableBindersAndWarranties.flatMap(
											(binderWarranty) => binderWarranty.warranties,
										),
									)}
									isSyndic={me?.role === RoleEnum.SYNDIC}
								/>
							);
						})}
				</div>

				<div className="flex justify-between">
					<Button variant="outline" onClick={() => navigate(-1)} disabled>
						Précédent
					</Button>
					<div className="flex gap-2">
						<Button variant="outline" disabled>
							Opération à compléter par le client
						</Button>
						<Button
							onClick={handleSave}
							disabled={
								updateBinderWarrantiesSelectedMutation.isPending ||
								localSelectedBinderWarrantyIds.length === 0
							}
						>
							Suivant
						</Button>
					</div>
				</div>
			</div>
		</PricingOptionsProvider>
	);
}
